import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  Output,
  ViewChild,
} from '@angular/core';
import { Router } from '@angular/router';
import {
  NbDialogService,
  NbSelectComponent,
  NbToastrService,
  NbToggleComponent,
} from '@nebular/theme';

import { ConfirmMessageDialogComponent } from 'app/@components/confirm-message-dialog/confirm-message-dialog.component';
import { NativeAppService } from 'app/@core/backend/common/services/native-app.service';
import {
  FitnessPlan,
  FitnessPlanData,
  MealPlan,
  MealPlanData,
} from 'app/@core/interfaces/common/CalorieFriend/plan';
import {
  FitnessPlanDay,
  MealPlanDay,
  Short_WeekDays,
} from 'app/@core/interfaces/common/CalorieFriend/plan-day';

import { MealPlanTemplateCreateComponent } from 'app/admin/public-templates/template-create/template-create.component';
import { Subject } from 'rxjs';
import { retryWhen } from 'rxjs-compat/operator/retryWhen';
import { takeUntil } from 'rxjs/operators';
import { FitnessPlanTemplateCreateComponent } from '../../../FitnessPlans/plan-template-create/plan-template-create.component';

@Component({
  selector: 'ngx-plan-list-item',
  templateUrl: './plan-list-item.component.html',
  styleUrls: ['./plan-list-item.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PlanListItemComponent implements OnDestroy {
  @Input() set isFitnessPlan(value: boolean) {
    this._isFitnessPlan = value;
    this._plansService = value
      ? this.fitnessPlansService
      : this.mealPlansService;
  }
  get isFitnessPlan(): boolean {
    return this._isFitnessPlan;
  }

  @Input() patientId: number;
  @Input() patientGuid: string;
  @Input() programMode: boolean;
  @Input() isProgramTemplate: boolean;
  @Input() set plan(value: FitnessPlan | MealPlan) {
    this._plan = value;
    this.loadAssignedWeekDays();
    this.isWaiting = false;
    this.cdRef.reattach();
    this.cdRef.markForCheck();
  }
  get plan(): FitnessPlan | MealPlan {
    return this._plan;
  }
  @Input() startDate: Date;
  @Input() endDate: Date;
  @Input() planIndex: number;
  @Input() programTemplateId: number;

  @Output() changed = new EventEmitter<FitnessPlan | MealPlan>();
  @Output() activeChanged = new EventEmitter<FitnessPlan | MealPlan>();
  @Output() duplicated = new EventEmitter<MealPlan | FitnessPlan>();
  @Output() deleted = new EventEmitter<number>();
  @Output() moved = new EventEmitter<number>();

  @ViewChild('activeToggle', { static: true }) activeToggle?: NbToggleComponent;
  @ViewChild('durationSelect', { static: false })
  durationSelect?: NbSelectComponent;

  private _isFitnessPlan: boolean = false;
  private _plansService: FitnessPlanData | MealPlanData;
  private _plan: FitnessPlan | MealPlan;

  isWaiting: boolean = true;
  assignedWeekDays: string;
  readonly durationOptions = [
    { label: '1 week', value: 1 },
    ...Array.from({ length: 15 }, (_, i) => i + 2).map((i) => ({
      label: `${i} weeks`,
      value: i,
    })),
  ];

  protected readonly unsubscribe$ = new Subject();

  constructor(
    private router: Router,
    public nativeAppService: NativeAppService,
    private fitnessPlansService: FitnessPlanData,
    private mealPlansService: MealPlanData,
    private dialogService: NbDialogService,
    private toastrService: NbToastrService,
    private cdRef: ChangeDetectorRef
  ) {
    console.log("plan list detach");
    this.cdRef.detach();
  }
  ngOnDestroy(): void {
    this.unsubscribe$.next();
  }

  onSend(): void {
    if (this.programMode) return;

    /*if (!this._plan.active) {
      this.onActiveChanged(true);
      return;
    }*/

    this.isWaiting = true;
    this.cdRef.markForCheck();

    this._plansService
      .submitPatientNotification(this.patientId, this._plan.id)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(() => this.toastrService.success('', 'Plan sent successfully'))
      .add(() => {
        this.isWaiting = false;
        this.cdRef.markForCheck();
      });
  }

  onActiveChanged(active: boolean): void {
    if (this.programMode) return;

    if (!this.plan.active) {
      this.plan.active = true;
      return;

    } 


    this.dialogService
      .open(ConfirmMessageDialogComponent, {
        closeOnEsc: false,
        closeOnBackdropClick: false,
        context: {
          Title: active ? 'Activate the plan' : 'Disable the plan',
          Message: active
            ? 'Are you sure you want to set this plan Active? This plan will become the default option for the patient zone and the user will be notified.'
            : 'Are you sure you want to set this plan Disabled?',
        },
      })
      .onClose.subscribe((ok: boolean) => {
        if (!ok) {
          this.activeToggle?.writeValue(!active);
          this.plan.active = false;
          return;
        }

        this.isWaiting = true;
        this.cdRef.markForCheck();

        this._plansService
          .activate(this._plan.id, active)
          .pipe(takeUntil(this.unsubscribe$))
          .subscribe(
            () => {
              this._plan.active = active;
              this.cdRef.markForCheck();
              this.activeChanged.emit(this._plan);
              if (!active) return;

              this._plansService
                .submitPatientNotification(this.patientId, this._plan.id)
                .pipe(takeUntil(this.unsubscribe$))
                .subscribe(() =>
                  this.toastrService.success('', 'Plan sent successfully')
                )
                .add(() => {
                  this.isWaiting = false;
                  this.cdRef.markForCheck();
                });
            },
            () => {
              this._plan.active = !active;
              this.cdRef.markForCheck();
              this.toastrService.danger('', 'Error in activating plan');
            }
          );
      });
  }

  onDurationChanged(value: number): void {
    if(this.isProgramTemplate) {
      this._plan.duration = value;
      this.changed.emit(this._plan);
      return;
    }
    this.isWaiting = true;
    this.cdRef.markForCheck();

    this._plansService
      .update({ ...this._plan, duration: value })
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(
        () => {
          this._plan.duration = value;
          this.changed.emit(this._plan);
        },
        () => {
          this.durationSelect?.writeValue(this._plan.duration);
          this.toastrService.danger('', 'Error in duration update');
        }
      )
      .add(() => {
        this.isWaiting = false;
        this.cdRef.markForCheck();
      });
  }

  onEdit(): void {
    if(this.isProgramTemplate) {
      this.router.navigateByUrl(
        `/pages/CalorieFriend/program-template/${this.isFitnessPlan ? 'fitness' : 'meal'
        }/${this.programTemplateId}/${this.planIndex}`
      );
    } else {
      this.router.navigateByUrl(
        `/pages/CalorieFriend/${this._isFitnessPlan ? 'Fitness' : 'Meal'
        }PlanEdit/${this._plan.id}`
      );
    }
  }

  onClientLinkClick(): void {
    this.nativeAppService.sendMessageToApp(
      'openblank',
      `/PatientView/Patient/${this.patientGuid}/${this._isFitnessPlan ? 'Workout' : 'Meal'
      }/${this._plan.id}`
    );
  }

  onSaveAsTemplate(): void {
    this.dialogService
      .open(ConfirmMessageDialogComponent, {
        closeOnEsc: true,
        closeOnBackdropClick: true,
        context: {
          Message: 'Are you sure to create a template from this plan?',
        },
      })
      .onClose.subscribe((res) => {
        if (!res) return;

        if (this._isFitnessPlan)
          this.dialogService.open(FitnessPlanTemplateCreateComponent, {
            closeOnEsc: false,
            context: {
              templatePlan: this._plan as FitnessPlan,
            },
          });
        else
          this.dialogService.open(MealPlanTemplateCreateComponent, {
            closeOnEsc: false,
            context: {
              templatePlan: this._plan as MealPlan,
            },
          });
      });
  }

  onDuplicate(): void {
    if(!this.isProgramTemplate) {
    this.isWaiting = true;
    this.cdRef.markForCheck();

    this._plansService
      .duplicate(this._plan.id)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((res) => this.duplicated.emit(res as (MealPlan | FitnessPlan)))
      .add(() => {
        this.isWaiting = false;
        this.cdRef.markForCheck();
      });
    } else {
      this.duplicated.emit(this._plan as (MealPlan | FitnessPlan))
    }
  }

  onDelete(): void {
    this.dialogService
      .open(ConfirmMessageDialogComponent, {
        closeOnEsc: true,
        closeOnBackdropClick: true,
        context: { Message: 'Are you sure to delete this plan?' },
      })
      .onClose.subscribe((res) => {
        if (!res) return;
        if(!this.isProgramTemplate) {
        this.isWaiting = true;
        this.cdRef.markForCheck();
        this._plansService
          .delete(this._plan.id)
          .pipe(takeUntil(this.unsubscribe$))
          .subscribe(
            () => {
              this.toastrService.success('', 'Sucessfully deleted Plan');
              this.deleted.emit(this._plan.id);
            },
            () =>
              this.toastrService.danger('', 'Problem occurred deleting Plan')
          )
          .add(() => {
            this.isWaiting = false;
            this.cdRef.markForCheck();
          });
        } else {
          this.deleted.emit(this._plan.id);
        }
      });
  }

  onUp(): void {
    this.moved.emit(-1);
  }

  onDown(): void {
    this.moved.emit(1);
  }

  private loadAssignedWeekDays(): void {
    const weekDays: boolean[] = new Array(7);

    this._plan.days.forEach((day: FitnessPlanDay | MealPlanDay) =>
      day.dayOfWeek.forEach((x) => (weekDays[x] = true))
    );
    const assignedDays: string[] = weekDays
      .map((x, i) => {
        if (x) return Short_WeekDays[i];
        return null;
      })
      .filter((x) => x);
    this.assignedWeekDays =
      assignedDays.length === 0 ? '' : `(${assignedDays.join(', ')})`;
  }
}
